/** @format */

// vendor
import { defineComponent, ref, reactive, computed, provide } from "@vue/composition-api";
// project
import * as StudyAPI from "@/services/api/studies";
import * as AssetAPI from "@/services/api/assets";
import { Asset, Study, AssetVersion, StudyDataChannel } from "@/psychlab/types";
// local
import { default as LangSelect } from "./header/lang-select.vue";
import { default as ShareLink } from "./header/share-link.vue";
import { default as StudyNavigation } from "./header/study-navigation.vue";
import { default as BlueprintInfo } from "./header/blueprint-info.vue";
import { default as StudyNameInput } from "./header/study-name-input.vue";
import { default as ToggleProgressVisible } from "./header/toggle-progress-visible.vue";
import { default as OpenToggle } from "./header/open-toggle.vue";
import { StudyContext } from "./StudyContext";
import { default as StudyTitle } from "./header/study-title.vue";

export type EditState = {
	editing: boolean;
	value: string;
};

export const ManageStudy = defineComponent({
	components: {
		LangSelect,
		ShareLink,
		StudyNavigation,
		BlueprintInfo,
		StudyNameInput,
		ToggleProgressVisible,
		OpenToggle,
		StudyTitle,
	},
	setup(props, context) {
		// const { studyId } = useRouteCustomParams(context);

		const studyId = computed(() => {
			const r = context.root.$route;
			return r.params["studyId"] || r.params["groupId"];
		});

		const blueprint = ref<Asset>();
		const blueprintVersion = ref<AssetVersion<any>>();
		const study = ref<Study>();
		const editingName = reactive<EditState>({
			editing: false,
			value: "",
		});
		const toggling = ref(false);

		const channels = ref<StudyDataChannel[]>([]);

		const studyContext = computed<StudyContext>(() => ({
			study,
			channels,
			blueprint,
			blueprintVersion,
		}));

		provide("study", study);
		provide("studyContext", studyContext);

		const studyLanguage = computed<string>({
			get() {
				return study.value ? study.value.target.language : "-";
			},
			set(v) {
				setLanguage(v);
			},
		});

		const showProgress = computed<boolean>({
			get() {
				if(!study.value){ return false; }
				return study.value.target.showProgress !== undefined ? study.value.target.showProgress : true;
			},
			set(v) {
				setProgressVisible(v);
			},
		});

		const setLanguage = async (v: string) => {
			if (!study.value) {
				return;
			}
			try {
				if (studyId) {
					await StudyAPI.setStudyLanguage(studyId.value, v);
					study.value.target.language = v;
				}
			} catch (err) {
				console.error(err);
			}
		};

		const setProgressVisible = async (v: boolean) => {
			if (!study.value) { return; }
			try {
				if (studyId) {
					await StudyAPI.setStudyProgressVisible(studyId.value, v);
					study.value.target.showProgress = v;
				}
			} catch (err) {
				console.error(err);
			}
		};

		const rename = (): void => {
			if (!study.value) {
				return;
			}
			editingName.editing = true;
		};

		const cancelRename = (): void => {
			editingName.value = study.value?.name || "";
			editingName.editing = false;
		};

		const confirmRename = async () => {
			if (!study.value || !editingName.value) {
				return;
			}
			const newName = editingName.value;
			try {
				if (studyId) {
					await StudyAPI.setStudyName(studyId.value, newName);
					study.value.name = newName;
				}
			} catch (err) {
				console.log(err);
			}
			editingName.editing = false;
		};

		const toggleEnabled = async () => {
			if (toggling.value) {
				return;
			}
			if (!study.value) {
				return;
			}
			toggling.value = true;
			const v = !study.value.enabled;
			try {
				if (studyId) {
					await StudyAPI.setStudyEnabled(studyId.value, v);
					study.value.enabled = v;
					study.value = JSON.parse(JSON.stringify(study.value));
				}
			} catch (err) {
				console.log(err);
			}
			toggling.value = false;
		};

		const init = async () => {
			if(!studyId.value){ return; }
			
			try {
				study.value = await StudyAPI.getStudy(studyId.value);

				editingName.value = study.value.name;

				blueprint.value = await AssetAPI.getAsset(
					study.value.target.study,
				);
				blueprintVersion.value = await AssetAPI.getAssetVersion(
					study.value.target.study,
					study.value.target.version,
					"+data +vIndex +name"
				);
				channels.value = await StudyAPI.getStudyDataChannels(studyId.value);
			}
			catch(err){
				console.log(err.message);
			}
		};

		init();

		return {
			study,
			studyId,
			blueprint,
			blueprintVersion,
			editingName,
			studyLanguage,
			showProgress,
			toggleEnabled,
			rename,
			cancelRename,
			confirmRename,
		};
	},
});
